import Vue from "vue";
import Vuex from "vuex";
import { main as axios, oauth as oauthAxios } from "../axios";
import createPersistedState from "vuex-persistedstate";
import VueJwtDecode from "vue-jwt-decode";

import router from "@/router/index.js";
import { getInstance } from "@/auth/index";

import UserService from "../services/UserService";
import WalletService from "../services/WalletService";
import WalletServiceV3 from "../services/WalletServiceV3";
import ClientServiceV2 from "../services/ClientServiceV2";
// const ClientServiceV2 = require("../services/ClientServiceV2");

// import ReportService from "../services/ReportService";
import MarqetaService from "../services/MarqetaService";
import TriggerService from "../services/TriggerService";
import CommunicationService from "../services/CommunicationService";
import FeatureFlagService from "../services/featureflags/FeatureFlagService";
import WhitelabelService from "../services/WhitelabelService";
import InformService from "../services/InformService";
// Lazy singleton — defers `new WhitelabelService()` (and its axios-client
// construction) until the first store action that needs it.  This ensures
// createAxiosInstance is fully defined before use, surviving webpack's
// ModuleConcatenationPlugin scope-hoisting in production builds.
let _whitelabelSvc;
const getWhitelabelSvc = () => (_whitelabelSvc ??= new WhitelabelService());
import AuthService from "@/services/AuthService";
import { applyBrandFavicons } from "../utils/brandFavicons";
import { clientPermissionGetters } from "./clientPermissionGetters";

// Bug #189562: setFeatureFlags can be dispatched from 3 unsynchronized
// triggers (auth/index.js, App.vue's onReady/onConfigurationChanged) that can
// overlap — see the staleness guard inside setFeatureFlags for why this exists.
let setFeatureFlagsCallId = 0;

Vue.use(Vuex);

localStorage.removeItem("failedLogin");

const storeDebugLog = (userId, key, value) => {
  var sup = {
    userId: userId,
    key: key,
    value: value,
  };
  UserService.createDebugUserSupplemental(sup).catch((error) => {
    console.log("Error creating user sup ", error);
  });
};

const defaultState = () => ({
  globalClientId: null,
  permissions: [],
  roles: [],
  featureFlags: {
    "feature-one10-points": false,
    "feature-one10-points-to-card": false,
    "feature-tax-info": false,
    "feature-company-merchandise": false,
    "feature-prismatic": false,
    "feature-admin-bulk-submissions": false,
    "feature-plaid-integration": false,
    "feature-power-bi-reports": false,
    "feature-performx-logo": false,
    "feature-psps-lookup": false,
    "feature-sso-setup-tickets": false,
    "feature-marqeta-card-products": false,
  },
  // Flags evaluated against `globalClientId` — the client selected in the dropdown —
  // rather than the signed-in user's own clientId, which is what `featureFlags` above
  // uses. Only the admin screens need this: there an internal admin configures some
  // OTHER client, so a client-targeted flag read off `featureFlags` answers for the
  // wrong tenant. Populated by `setClientFeatureFlags`, which is lazy and refreshes
  // whenever `setClientId` moves.
  clientFeatureFlags: {
    "feature-engage-users-sync-to-inform": false,
    "feature-dynamic-program-builder-claims": false,
  },
  // Which client the set above was evaluated for, or null when it never has been.
  // The flags alone cannot say: every one of them defaults to false, so "off for this
  // client", "not loaded yet" and "loaded for a different client" are one value. A
  // screen that renders a flag-dependent layout has to tell those apart or it renders
  // the off-branch first and corrects itself a moment later.
  clientFeatureFlagsClientId: null,
  // The selected client's Engage merge configuration, as daas-engage-integration holds
  // it. Only two fields are read: `ExternalPropertyMap` says which Engage properties a
  // client feed supplies, and `AllowUserOverrideProperties` says which of those Engage
  // still wins. Together they decide whether editing a field in Whistle would survive
  // the next sync pass or be quietly reverted — see the `informOverride` mixin.
  //
  // Not persisted, for the same reason the flag set is not: it is a remote answer about
  // a tenant, and a cached one is a claim about a session that has ended.
  engageConfiguration: null,
  engageConfigurationClientId: null,
  // The client a fetch is in flight FOR, not a bare "is loading". A boolean cannot say
  // which client it is loading, so a client switch during a fetch would see "already
  // loading", skip its own fetch, and then take delivery of the previous client's
  // answer — with nothing left to retry it.
  engageConfigurationLoadingClientId: null,
  token: null,
  tokenExpiration: null,
  tokenRefreshTask: null,
  magicLinkToken: null,
  magicLinkTokenExpiration: null,
  magicLinkTokenRefreshTask: null,
  magicLinkTokenExpirationWarning: false,
  magicLinkRoute: null,
  userProfile: null,
  walletProfile: {
    loading: false,
    transactions: [],
    paymentRules: [],
  },
  clients: [],
  cashBalance: 0,
  pointsBalance: 0,
  // pendingBalance: 0,
  balances: [],
  loadingBalance: false,
  marqeta: {
    cards: [],
    canRegister: false,
    alreadyRegister: false,
    processingKYB: false,
    passedKYB: false,
    loaded: false,
  },
  impersonate: null,
  loaded: false,
  failedLogin: false,
  searchArray: [],
  loadItemFromSearch: null,
  displayWalletMenu: false,
  displayMobilePopup: false,
  walletMenuScreen: "main",
  walletInstructionTab: 0,
  // Used so we know if the user clicked the wallet button in the nav bar or if we're toggling it from the wallet page
  sourceWalletPage: null,
  profilePageTab: 0,
  postItArray: [],
  reportTokens: [],
  // Used to save preference for list vs card view
  activity: {
    listView: false,
    showAdminOptions: true,
    unread: 0,
    unreadRefreshTask: null,
  },
  // Used to store slack correlationId if user routes to us from Slack and has to go log in.
  slackWorkspaceCorrelationId: null,
  snackbar: {
    show: false,
    text: "",
    color: "info", // Default color
    timeout: 4000, // Default timeout (ms)
  },
  pendingUserCount: {
    clientId: null,
    count: null,
  },
  appNotification: null,
  dashboardConfig: null,
  whitelabel: {
    // Raw config only — display defaults live in whitelabelMixin, so every
    // fallback is in one place rather than split across store and component.
    applicationName: null,
    supportEmail: null,
    logoUrl: null,
    faviconUrl: null,
    appStoreUrl: null,
    googlePlayStoreUrl: null,
    // ADO #190097: true when GET-by-domain resolved the clientId=0 default.
    isDefault: false,
    loading: false,
    error: null,
  },
  userPermissionsLoading: false,
  // false until fetchWhitelabelConfig settles (success or fatal error).
  // App.vue gates login/shell on this so auth/cached-profile cannot unlock early.
  whitelabelReady: false,
  failedWhitelabel: false,
  failedPermissions: false,
  // Bug #189562 (Cycle 5): true only once setFeatureFlags has committed real,
  // SDK-confirmed-ready flag data — replaces FeatureFlagService.isReady() as
  // the "is branding data trustworthy" gate, since that latch never lowers
  // again once flipped (including across LOGOUT). Reset to false on LOGOUT
  // via defaultState().
  featureFlagsResolved: false,
  // True when FeatureFlagService.initialize() rejects or ProviderEvents.Error fires.
  // Hard failure path: provider connection broken, not just slow/pending.
  // Reset to false on LOGOUT via defaultState(). Excluded from persistence.
  featureFlagsFailed: false,
});

/**
 * What survives a page reload.
 *
 * Exported so tests exercise the real rule rather than a copy — a copy silently stops
 * describing the store the moment either side changes, which is how a stale
 * client-scoped flag set reached a rendered screen.
 *
 * Everything persists by default; the deletions below are the exceptions.
 */
export const persistedStateReducer = (state) => {
  const persisted = { ...state };
  delete persisted.dashboardConfig;
  // Ephemeral Tier-1/2 gate flags — must NOT survive reload or the app
  // unlocks from a prior session while fetchWhitelabelConfig is still in flight.
  delete persisted.whitelabelReady;
  delete persisted.failedWhitelabel;
  delete persisted.failedPermissions;
  // Must not survive reload. A rehydrated true makes fetchUserPermissions
  // return before the request and before the JWT fallback, permanently.
  delete persisted.userPermissionsLoading;
  delete persisted.whitelabel;
  delete persisted.featureFlagsResolved;
  delete persisted.featureFlagsFailed;
  // Same reason as the three above. The client-scoped flag set is answered by a
  // flagd provider held in a module variable inside FeatureFlagService, and that
  // variable does NOT survive a reload — so a rehydrated set is a value with no
  // provider behind it, decided for a session that has ended. Worse, keeping the
  // id made the "has this resolved yet" check pass on the stale set, which is the
  // one thing it exists to prevent. Dropped so a reload genuinely starts unresolved
  // and the screen waits, rather than rendering last session's answer.
  delete persisted.clientFeatureFlags;
  delete persisted.clientFeatureFlagsClientId;
  delete persisted.engageConfiguration;
  delete persisted.engageConfigurationClientId;
  delete persisted.engageConfigurationLoadingClientId;
  // The cards are kept for a fast first paint, but "loaded" means this session's
  // GET /cards answered. Rehydrated as true, copy gated on it (the issuer statement,
  // the card name) would show last session's product before the reload's request returns.
  if (persisted.marqeta) persisted.marqeta = { ...persisted.marqeta, loaded: false };
  return persisted;
};

// Named export so test files can import and exercise the REAL action functions.
const actions = {
  // getPermissions({ commit }, userId) {
  //   console.log(userId);
  //   return axios
  //     .get(process.env.VUE_APP_USER_URL + "/userPermissions/" + userId)
  //     .then(({ data }) => {
  //       console.log("returned data is: ", data);
  //       commit("SET_PERMISSION_DATA", data);
  //     });
  // },
  resetState({ commit }) {
    commit("RESET_STATE");
    return;
  },
  logout({ commit }) {
    commit("LOGOUT");
    return;
  },
  setErrorString({ commit }, state) {
    commit("SET_ERROR_STRING", state);
  },
  setClientId({ commit, dispatch }, clientId) {
    console.log("Setting global clientId", clientId);
    commit("SET_CLIENT_ID", clientId);
    // Keep the client-scoped flag set pointed at the newly selected client. A no-op
    // until an admin screen has initialized it, so an ordinary session never opens the
    // second flagd connection just by having a clientId set at login.
    dispatch("setClientFeatureFlags");
    // Same reason: the merge configuration is per tenant, so a client change makes the
    // cached one an answer about a different client.
    commit("SET_ENGAGE_CONFIGURATION", { configuration: null, clientId: null });
  },
  async setFeatureFlags({ commit, state, dispatch }) {
    // Once the app has given up and committed to Whistle (hardFailureRecovery),
    // isReady() can still flip true later if the underlying connection
    // eventually succeeds in the background. setFeatureFlags is dispatched
    // from three independent places (this action's own callers, App.vue's
    // onReady/onConfigurationChanged, and auth/index.js) — auth/index.js has
    // no knowledge of App.vue's own "already gave up" guard, so that check
    // alone cannot stop a late success from re-opening a decision the user
    // has already seen. This is the one place all three funnel through.
    if (state.featureFlagsFailed) return;

    const clientId = state.userProfile?.clientId;
    const context = {
      env: process.env.VUE_APP_ENVIRONMENT,
      // Always send hostname, including pre-auth. feature-performx-logo (and
      // any other host-targeted flag) cannot wait for clientId — login and
      // favicons resolve before a profile exists. String, same as clientId:
      // flagd `in` is strict equality.
      hostname: window.location.hostname,
    };
    if (clientId) {
      // String, not the number Whistle stores. flagd targeting is JSON Logic `in`,
      // which is strict equality, so a numeric clientId matches no allowlist entry and
      // is indistinguishable from a client that was never listed. Every client-targeted
      // flag in this set read false until this was stringified.
      context.clientId = String(clientId);
    }

    // Bug #189562: clearContext/setContext act on one shared client, so an
    // overlapping dispatch's clearContext() can wipe context out from under
    // this call between here and the flag reads below. Only the LATEST
    // dispatch may apply its result — a stale one abandons instead of
    // repainting the wrong brand after a correct dispatch already applied.
    const callId = ++setFeatureFlagsCallId;

    await FeatureFlagService.clearContext();
    await FeatureFlagService.setContext(context);

    if (callId !== setFeatureFlagsCallId) return;

    const flags = Object.keys(state.featureFlags).reduce((acc, key) => {
      acc[key] = FeatureFlagService.getFeatureFlagValue(key);
      return acc;
    }, {});

    commit("SET_FEATURE_FLAGS", flags);
    // Bug #189562 (Cycle 5): only true when FeatureFlagService.isReady() is
    // true at this exact commit time — never off the back of a premature
    // commit (see featureFlagsResolved.test.js for the race this guards).
    const flagsAreReady = FeatureFlagService.isReady();
    commit("SET_FEATURE_FLAGS_RESOLVED", flagsAreReady);
    // Load-bearing: flags stay false until initialize(), so the unordered auth/index.js dispatch can't read whitelabel early.
    const performxLogoOn = !!flags["feature-performx-logo"];
    // Bug #189562 (follow-up): setFeatureFlags can also be dispatched (from
    // auth/index.js) before FeatureFlagService.initialize() has completed its
    // ready-handshake and/or before fetchWhitelabelConfig resolves. The static
    // index.html favicon is an intentionally blank/neutral placeholder (not
    // any real brand, since commit 45b1d3f6) — setFeatureFlags is what applies
    // the correct final brand (Whistle, PerformX, or tenant) once BOTH: (1)
    // the feature flag is confirmed trustworthy via FeatureFlagService.isReady()
    // — a `false` read from a not-yet-ready SDK is indistinguishable from a
    // genuinely-off flag, so nothing can be applied until readiness is known —
    // and (2), if the flag turns out to be on, whitelabel config has also
    // settled (state.whitelabelReady), since applying PerformX defaults early
    // could later be wrong once the tenant's real faviconUrl is known. The
    // later setFeatureFlags dispatch (after FeatureFlagService.initialize(),
    // which App.vue only reaches once fetchWhitelabelConfig has settled)
    // applies the correct branding once both conditions hold.
    if (!FeatureFlagService.isReady() || (performxLogoOn && !state.whitelabelReady)) {
      // Defer: skip for now, correct value applied once both conditions hold.
    } else {
      const tenantFaviconUrl = performxLogoOn ? state.whitelabel?.faviconUrl ?? null : null;
      applyBrandFavicons(performxLogoOn, tenantFaviconUrl);
    }
    // Read dashboardConfig AFTER setContext resolves so flagd evaluates under
    // the correct client context (fixes the cold-load race from Story #184373).
    if (clientId) {
      dispatch("fetchDashboardConfig");
      // Same ordering constraint as fetchDashboardConfig above: the banner flag
      // is client-targeted, so reading it before setContext resolves evaluates
      // against the wrong context and can serve another tenant's announcement.
      dispatch("fetchAppNotification");
    }
  },

  /**
   * Evaluate the client-scoped flag set against the currently selected client.
   *
   * Separate from `setFeatureFlags` on purpose. That one owns the app-wide set and
   * carries side effects — favicons, the platform banner, the dashboard config — so
   * re-running it on every client switch would repaint an admin's own chrome with the
   * tenant they happen to be looking at. This one commits flags and nothing else.
   *
   * `initialize` true opens the second flagd connection if it is not already open;
   * leave it false (the default) to refresh only when an admin screen has already
   * asked for it. That is why `setClientId` can dispatch this unconditionally.
   */
  async setClientFeatureFlags({ commit, state }, { initialize = false } = {}) {
    if (!initialize && !FeatureFlagService.isClientScopedInitialized()) return;

    const clientId = state.globalClientId ?? state.userProfile?.clientId;
    const context = { env: process.env.VUE_APP_ENVIRONMENT };
    if (clientId !== null && clientId !== undefined) {
      // String, not the number Whistle stores. flagd targeting is JSON Logic `in`,
      // which is strict equality, so a numeric clientId misses every allowlist entry
      // and is indistinguishable from simply not being allowlisted.
      context.clientId = String(clientId);
    }

    if (FeatureFlagService.isClientScopedInitialized()) {
      await FeatureFlagService.setClientScopedContext(context);
    } else {
      await FeatureFlagService.initializeClientScoped(context);
    }

    const flags = Object.keys(state.clientFeatureFlags).reduce((acc, key) => {
      acc[key] = FeatureFlagService.getClientScopedFeatureFlagValue(key);
      return acc;
    }, {});

    // The id is committed with the values it produced, so a reader can check the two
    // agree. Committing it separately would let a screen read this client's id beside
    // the previous client's flags.
    commit("SET_CLIENT_FEATURE_FLAGS", { flags, clientId });
  },
  /**
   * Load the selected client's Engage merge configuration, once per client.
   *
   * Cached because both the people drawer and the profile page ask for it, and the
   * profile page is every user rather than only admins — a fetch per mount would put a
   * DaaS round trip behind every profile view. Re-fetched when the client moves,
   * because the answer is per tenant.
   *
   * Never throws. A tenant with no configuration, an unconfigured client, or a proxy
   * failure all resolve to null, which the consuming mixin reads as "no feed owns
   * anything" — the same answer a client that has never used Inform gets.
   */
  async fetchEngageConfiguration({ commit, state }, { clientId } = {}) {
    const targetClientId = clientId ?? state.globalClientId ?? state.userProfile?.clientId;
    if (targetClientId === null || targetClientId === undefined) return;

    const target = String(targetClientId);
    // Already answered, or already being answered FOR THIS CLIENT. Both tests name the
    // client: a bare loading flag would turn a switch mid-fetch into a permanently
    // stale answer, because the new client's call returns here and never comes back.
    if (state.engageConfigurationClientId === target) return;
    if (state.engageConfigurationLoadingClientId === target) return;

    commit("SET_ENGAGE_CONFIGURATION_LOADING", target);
    try {
      const configuration = await new InformService().getEngageConfiguration({
        clientId: targetClientId,
      });
      // Superseded responses are DROPPED, not committed. Ordering by arrival would let
      // a slow answer for the client we just left overwrite the one we are on — the
      // same stale state the guard above prevents, arriving by the other route.
      if (state.engageConfigurationLoadingClientId === target) {
        commit("SET_ENGAGE_CONFIGURATION", {
          configuration: configuration ?? null,
          clientId: targetClientId,
        });
      }
    } catch (error) {
      // A client with no Inform tenant answers 404 here, which is an ordinary state and
      // not worth surfacing. Commit null so the pair still agrees and the screen stops
      // waiting rather than retrying on every render.
      console.log("Could not load Engage configuration", error?.response?.status ?? error);
      if (state.engageConfigurationLoadingClientId === target) {
        commit("SET_ENGAGE_CONFIGURATION", {
          configuration: null,
          clientId: targetClientId,
        });
      }
    } finally {
      // Only if no later fetch has taken over. Clearing unconditionally would let a
      // superseded response reopen the gate for a call already in flight.
      if (state.engageConfigurationLoadingClientId === target) {
        commit("SET_ENGAGE_CONFIGURATION_LOADING", null);
      }
    }
  },
  fetchDashboardConfig({ commit }) {
    commit("SET_DASHBOARD_CONFIG", FeatureFlagService.getDashboardConfig());
  },
  hardFailureRecovery({ commit, state }) {
    // Called only once the full 5s budget has run out with no real answer —
    // not on individual failed attempts, which may just be a slow/throttled
    // connection still working. Settles the gate with Whistle defaults;
    // permanent for this session — setFeatureFlags refuses to run again once
    // featureFlagsFailed is true, so a later success is never applied.
    const flags = Object.keys(state.featureFlags).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});

    commit("SET_FEATURE_FLAGS", flags);
    commit("SET_FEATURE_FLAGS_RESOLVED", true);
    applyBrandFavicons(false, null);
  },
  async setUser({ commit, state, dispatch, getters }, user) {
    let userId = user;
    commit("SET_ERROR_STRING", "get-user-" + userId);

    console.log("Getting user: " + userId);
    commit("SET_ERROR_STRING", "get-user2");
    await UserService.getUserProfileV3(state.magicLinkToken, { version: 2 })
      .then((response) => {
        commit("SET_ERROR_STRING", "pre-dispatch");
        commit("SET_USER", response);
        if (
          state.globalClientId == null ||
          state.globalClientId == undefined ||
          !getters.hasPermissionToViewClients()
        )
          dispatch("setClientId", response.clientId);
        commit("SET_ERROR_STRING", "post-dispatch");
        this.dispatch("setBalances", response.userId);
        commit("SET_ERROR_STRING", "setBalances");
        this.dispatch("setFailedLogin", false);
        commit("SET_ERROR_STRING", "complete-no-fail");
        return;
      })
      .catch((error) => {
        commit("SET_ERROR_STRING", "catch-" + JSON.stringify(error).substring(0, 150));
        console.log("Error fetching user!", error);
        this.dispatch("setFailedLogin", true);
        // Now route user to login error page
        router.app.$router.push({ name: "login-error" });

        // storeDebugLog(userId, "Vue_Set_User_Fail_1", error);
        storeDebugLog(userId, "Vue_Set_User_Fail_2", JSON.stringify(error));
        storeDebugLog(
          userId,
          "Vue_Set_User_Fail_3",
          error.error ? JSON.stringify(error.error) : "no error.error"
        );
        storeDebugLog(
          userId,
          "Vue_Set_User_Fail_4",
          JSON.stringify(error, Object.getOwnPropertyNames(error))
        );
        storeDebugLog(
          userId,
          "Vue_Set_User_Fail_5",
          JSON.stringify(error && error.response ? error.response.data || error.response : error)
        );
        return;
      });
  },
  async setClients({ commit, getters }) {
    if (!getters.hasPermissionToViewClients()) return;
    // imported here because V2 services rely on the store, so we don't want a ciruclar dependency
    const clientService = new ClientServiceV2();

    try {
      var response = await clientService.getClientDropdownV3();
      var clients = response.result.rows.map((x) => {
        var obj = x;
        obj.formattedName = x.clientId + " - " + x.clientName;
        return obj;
      });

      commit("SET_CLIENTS", clients);
    } catch (err) {
      console.log("Error getting clients ", err);
    }
    // }
  },
  async setWalletProfile({ commit, dispatch }) {
    commit("SET_WALLET_PROFILE_LOADING", true);
    try {
      const walletServiceV3 = new WalletServiceV3();
      const response = await walletServiceV3.getWalletProfile();
      if (!response || !response.result || !response.result.success) {
        dispatch("showError", {
          title: "Error",
          message: "We encountered a problem refreshing your wallet transactions.",
        });
      }

      commit("SET_WALLET_PROFILE", response.result);
    } catch (error) {
      console.log("Error fetching wallet profile!", error);
      dispatch("showError", {
        title: "Error",
        message: "We encountered a problem refreshing your wallet transactions.",
      });
    } finally {
      commit("SET_WALLET_PROFILE_LOADING", false);
    }
  },
  setToken({ commit, dispatch }, token) {
    console.log("Storing new token");
    commit("SET_TOKEN", token);
    commit("SET_PERMISSION_DATA", token);

    // Now we want to setup a timeout so it sleeps and fetches a new token right before it expires;
    dispatch("autoRefreshToken");

    if (!axios.interceptors.response.handlers.length)
      axios.interceptors.response.use(undefined, async (error) => {
        return new Promise(function (resolve, reject) {
          if (
            error.config &&
            error.response &&
            error.response.status === 401 &&
            !error.config.__isRetry
          ) {
            console.log("Refreshing token due to 401 error");
            dispatch(
              "autoRefreshToken",
              function (newToken) {
                console.log("inside autoRefreshToken callback ", newToken);
                error.config.__isRetry = true;
                error.config.headers["Authorization"] = `Bearer ${newToken}`;
                axios(error.config).then(resolve, reject);
              },
              function (
                flag // true = invalid session, false = something else
              ) {
                console.log("Could not refresh token");
                reject(flag);
              }
            );
          } else throw error;
        });
      });

    return;
  },
  autoRefreshToken({ commit, state, dispatch }, refreshNowCallback) {
    const { tokenExpiration } = state;
    const now = Date.now() / 1000; //exp is represented in seconds since epoch
    let timeUntilRefresh = tokenExpiration - now;
    // if (timeUntilRefresh < 4 * 60) timeUntilRefresh -= 5;
    //if token will last less than 5 min then we just fetch 20 seconds before the token expires (mainly for testing)
    // else

    if (timeUntilRefresh > 3 * 60) timeUntilRefresh -= 3 * 60; //refreshes 3 minutes before it expires

    // This is called from the axios interceptor if we're getting a 401, so we immediately refresh and set the timeout to 0
    if (refreshNowCallback) timeUntilRefresh = 0;

    console.log("Calling autoRefresh in ", timeUntilRefresh);
    const refreshTask = setTimeout(async () => {
      // dispatch("refreshTokens", creds);
      try {
        // We try and get the token silently.
        // This is where Auth0 will block the user if their session has expired or are blocked.
        const instance = getInstance();
        const newToken = await instance.getTokenSilently({
          ignoreCache: true,
        });
        // If a success, then we store the token and return so the app can continue
        dispatch("setToken", newToken);
        if (refreshNowCallback) refreshNowCallback(newToken);
        return newToken;
      } catch (err) {
        // There was an error. Either the user's session expired or they're blocked.
        console.log("Error refreshing token ", err);
        const instance = getInstance();
        console.log("Calling redirect inside index.js autoRefreshToken");
        instance.markAsUnauthenticated();
        instance.loginWithEmbedded({
          connection: process.env.VUE_APP_ENVIRONMENT,
          appState: {
            targetUrl: router.app.$route.fullPath,
          },
        });
      }
    }, timeUntilRefresh * 1000);
    // Clear any old timeouts before continuing
    if (state.tokenRefreshTask) {
      clearTimeout(state.tokenRefreshTask);
    }
    commit("SET_TOKEN_REFRESH_TASK", refreshTask);
  },
  async refreshWallet({ dispatch, commit }) {
    try {
      commit("SET_LOADING_BALANCE", true);
      dispatch("setMarqeta");
      await dispatch("setWalletProfile");
      commit("SET_LOADING_BALANCE", false);
    } catch (e) {
      console.log("Error refreshing wallet ", e);
      commit("SET_LOADING_BALANCE", false);
    }
  },
  async setBalances({ commit, state }, userId) {
    console.log("Getting user wallet: " + userId);
    // V2 vs V1. V2 relies solely on Stellar balance and starting out is just used for Bayer
    // var version =
    //   this.state.userProfile &&
    //   ((this.state.userProfile.businessEmail &&
    //     this.state.userProfile.businessEmail.endsWith("@wewhistle.com")) ||
    //     this.state.userProfile.clientId == 166)
    //     ? 2
    //     : 1;
    await WalletService.checkUserBalanceV2(userId, state.magicLinkToken)
      .then((response) => {
        // var cash = response.find(x => x.asset_code === "WhistleNotes");
        const cash = response.find((x) => x.awardId == 1 && x.type == "DEFAULT");
        const balance = cash ? cash.balance : 0;
        // console.log({ cash });
        commit("SET_CASH_BALANCE", balance);

        const otherBalances = response.filter((x) => x.awardId != 1);
        commit("SET_BALANCES", otherBalances);

        const pointsTotal = otherBalances
          .filter((x) => x.cashValuePoints === true)
          .reduce((sum, award) => sum + (award.balance || 0), 0);
        commit("SET_POINTS_BALANCE", pointsTotal);

        return response;
      })
      .catch((error) => {
        console.log("Error fetching user balance!", (error && error.response) || error);
      });
  },
  async setMagicLinkToken({ commit, dispatch }, data) {
    try {
      commit("SET_ERROR_STRING", "get-magic-link-" + data.magicLinkId);
      console.log("Magic link ID ", data.magicLinkId);
      console.log("Magic link  ", data.magicLink);

      console.log("Getting magic link: ", data.magicLink);
      commit("SET_ERROR_STRING", "get-magic-link2");
      let res = await UserService.getMagicLinkTokenV3(data.magicLinkId, data.magicLink);

      console.log("Got magic link response ", res);
      if (res.token) {
        commit("SET_MAGIC_LINK_TOKEN", res.token.access_token);
        commit("SET_PERMISSION_DATA", res.token.access_token);
        commit("SET_MAGIC_LINK_ROUTE", res.magicLink.redirectUri);
        let now = new Date().getTime() / 1000;
        let expiration = now + res.token.expires_in;
        console.log(`Magic link expiration: Current date - ${now}; Expires in ${expiration}`);
        commit("SET_MAGIC_LINK_EXPIRATION", expiration);
        // Set up timer to display session timeout error
        dispatch("watchMagicLinkExpiration");

        return res;
      } else throw "Missing token";
    } catch (err) {
      console.log("Error getting magic link token ", err);
      throw err;
    }
  },
  watchMagicLinkExpiration({ commit, state }) {
    const { magicLinkTokenExpiration } = state;
    const now = Date.now() / 1000; //exp is represented in seconds since epoch
    let timeUntilRefresh = magicLinkTokenExpiration - now;
    timeUntilRefresh -= 15; //refreshes 15 seconds before it expires

    console.log("Calling magic link autoRefresh in ", timeUntilRefresh);
    const refreshTask = setTimeout(async () => {
      try {
        commit("SET_MAGIC_LINK_EXPIRATION_WARNING", true);
      } catch (err) {
        // There was an error. Either the user's session expired or they're blocked.
        console.log("Error showing magic link display pop up ", err);
      }
    }, timeUntilRefresh * 1000);
    // Clear any old timeouts before continuing
    if (state.magicLinkTokenRefreshTask) {
      clearTimeout(state.magicLinkTokenRefreshTask);
    }
    commit("SET_MAGIC_LINK_TOKEN_REFRESH_TASK", refreshTask);
  },
  setMagicLinkTokenExpirationWarning({ commit }, val) {
    commit("SET_MAGIC_LINK_EXPIRATION_WARNING", val);
  },
  setLoaded({ commit }, loadingState) {
    commit("SET_LOADED", loadingState);
  },
  setFailedLogin({ commit }, loginState) {
    commit("SET_FAILED_LOGIN", loginState);
  },
  setSearchArray({ commit }, array) {
    commit("SET_SEARCH_ARRAY", array);
  },
  setLoadItemFromSearch({ commit }, id) {
    commit("SET_LOAD_ITEM_FROM_SEARCH", id);
  },
  setDisplayWalletMenu({ commit }, value) {
    console.log("setting wallet ", value);
    commit("SET_DISPLAY_WALLET_MENU", value);
  },
  setWalletMenuScreen({ commit }, value) {
    console.log("setting wallet menu screen ", value);
    commit("SET_WALLET_MENU_SCREEN", value);
  },
  setWalletInstructionTab({ commit }, value) {
    commit("SET_WALLET_INSTRUCTION_TAB", value);
  },
  setConfettiPlayed({ commit }, value) {
    console.log("setting confetti played date ", value);
    commit("SET_CONFETTI_PLAYED", value);
  },
  setSourceWalletPage({ commit }, value) {
    commit("SET_SOURCE_WALLET_PAGE", value);
  },
  setPostItArray({ commit }, noteId) {
    console.log("Updating post-it note array for entry: ", noteId);
    commit("SET_POST_IT_ARRAY", noteId);
  },
  setProfilePageTab({ commit }, tab) {
    commit("SET_PROFILE_TAB", tab);
  },
  setActivityListView({ commit }, bool) {
    commit("SET_ACTIVITY_LIST_VIEW", bool);
  },
  setActivityAdminOptions({ commit }, bool) {
    commit("SET_ACTIVITY_ADMIN_OPTIONS", bool);
  },
  pushReportTokens({ commit }, item) {
    commit("PUSH_REPORT_TOKENS", item);
  },
  setMarqeta({ commit, state }) {
    var object = {
      cards: [],
      alreadyRegistered: false,
      canRegister: false,
      processingKYB: false,
      passedKYB: false,
      loaded: false,
    };
    if ((state.userProfile && state.userProfile.firstLogin) || state.magicLinkToken) {
      // If their first login, reset Marqeta state while waiting for endpoint
      console.log("COMMITTING MARQETA");
      commit("SET_MARQETA", object);
    }
    return MarqetaService.getCards(state.magicLinkToken)
      .then((response) => {
        var event = {
          event: "GET_MARQETA_CARDS",
          source: "WHISTLE_AUTH",
          category: "PLATFORM",
          date: new Date(),
        };
        console.log("Logging event ", event.event);
        TriggerService.createActivity(event, state.magicLinkToken).catch((error) => {
          console.log("Error creating activity ", error.response);
        });
        // var business = response.business;
        // console.log("Marqeta card response: ", cards);
        if (response.state) object = response.state;

        if (response.error_code) {
          // object.cards = [];
          object.error_code = response.error_code;
          // If the user isn't set up, that means that we already got passed the business validation
          // if (response.error_code == 1190004) {
          //   object.alreadyRegistered = false;
          //   object.canRegister = true;
          //   object.processingKYB = false;
          // } else if (
          //   response.error_code == 1190019 ||
          //   response.error_code == 1190020
          // ) {
          //   // If the business isn't set up or they haven't passed KYB, then we don't let the user do anything
          //   // 1190020 means the user hasn't passed KYC yet so we stop them as well (this shouldn't happen)
          //   object.alreadyRegistered = false;
          //   object.canRegister = false;
          //   object.processingKYB = true;
          // } else if (response.error_code == 1190005) {
          //   object.processingKYB = false;
          //   object.alreadyRegistered = false;
          //   object.canRegister = false;
          // }
        } else {
          // var cards = response.cards;

          // // We've already found the user and the business in the database so they are already registered
          // object.cards = cards;
          // object.canRegister = true;
          // object.processingKYB = false;
          // object.alreadyRegistered = true;
          object.error_code = null;
        }
        // if (business.businessToken === business.actingBusinessToken) {
        //   object.submittedKYB = true;
        // }
        // if (business.status == "Pending") object.processingKYB = true;
        object = { ...object, loaded: true };
        commit("SET_MARQETA", object);
        return object;
      })
      .catch((error) => {
        console.log("Error fetching cards: ", error);
        var event = {
          event: "GET_MARQETA_CARDS_ERROR",
          source: "WHISTLE_AUTH",
          category: "PLATFORM",
          date: new Date(),
        };
        console.log("Logging event ", event.event);
        TriggerService.createActivity(event, state.magicLinkToken).catch((error) => {
          console.log("Error creating activity ", error.response);
        });
        // Stays not loaded, so the card name and issuer statement stay hidden rather than showing the default card's.
        commit("SET_MARQETA", object);
        return object;
      });
  },
  setSlackWorkspaceCorrelationId({ commit }, val) {
    commit("SET_SLACK_WORKSPACE_CORRELATION_ID", val);
  },
  setDisplayMobilePopup({ commit }, val) {
    commit("SET_DISPLAY_MOBILE_POPUP", val);
  },
  setImpersonate({ commit }, val) {
    commit("SET_IMPERSONATE", val);
  },
  async setUnreadMessages({ commit, state, dispatch }) {
    // Magic links don't show the nav so we don't need to get unreads
    if (state.magicLinkToken) return;
    let unread = 0;
    let timeout = 60000; // default of 60s
    try {
      let response = await CommunicationService.getUnreadMessagesV3(state.magicLinkToken, {
        sourceUser: state?.userProfile?.userId,
      });
      console.log("Got unread message response ", response);
      if (response && response.result) unread = response.result.count || 0;
      if (response && response.result && response.result.timeout_ms)
        timeout = response.result.timeout_ms;
    } catch (e) {
      console.log("Error getting unread message count: ", e);
    } finally {
      commit("SET_ACTIVITY_UNREAD", unread);

      console.log("Timeout? ", timeout);
      const refreshTask = setTimeout(() => {
        console.log("Dispatching setUnreadMessages after timeout");
        dispatch("setUnreadMessages");
      }, timeout);

      // Clear any old timeouts before continuing
      if (state.activity.unreadRefreshTask) {
        console.log("Clearing old activity timeout");
        clearTimeout(state.activity.unreadRefreshTask);
      }
      commit("SET_ACTIVITY_UNREAD_REFRESH_TASK", refreshTask);
    }
  },

  // ***** Snackbar actions *****
  // Action to show a generic snackbar message
  showSnackbar({ commit }, payload) {
    // Payload example: { text: 'Operation successful!', color: 'success' }
    commit("SET_SNACKBAR", payload);
  },
  // Action specifically for showing error messages
  showError({ commit }, { title = "Error", message = "An unexpected error occurred." }) {
    commit("SET_SNACKBAR", {
      text: `${title}: ${message}`, // Combine title and message for snackbar
      color: "red", // Set color to error
      timeout: 6000, // Maybe show errors for longer
    });
  },
  // Action specifically for showing success messages
  showSuccess({ commit }, { message = "Success!" }) {
    commit("SET_SNACKBAR", {
      text: message,
      color: "success",
      timeout: 3000,
    });
  },
  // Action to hide the snackbar (can be called by components if needed)
  hideSnackbar({ commit }) {
    commit("HIDE_SNACKBAR");
  },
  // Action to set pending user count directly (avoids extra API call)
  setPendingUserCount({ commit, state }, count) {
    commit("SET_PENDING_USER_COUNT", {
      clientId: state.globalClientId,
      count: count,
    });
  },
  // Action to fetch pending user count
  async fetchPendingUserCount({ commit, state }) {
    if (!state.permissions.includes("vue:read:pendingusers")) {
      commit("SET_PENDING_USER_COUNT", {
        clientId: state.globalClientId,
        count: null,
      });
      return;
    }
    if (
      state.pendingUserCount.count !== null &&
      state.pendingUserCount.clientId === state.globalClientId
    )
      return;
    try {
      const response = await UserService.getPendingUsers({
        status: "Pending",
        clientId: state.globalClientId,
        limit: 1,
        page: 1,
      });
      commit("SET_PENDING_USER_COUNT", {
        clientId: state.globalClientId,
        count: response.totalCount || 0,
      });
    } catch (error) {
      console.error("Error fetching pending user count", error);
      commit("SET_PENDING_USER_COUNT", {
        clientId: state.globalClientId,
        count: null,
      });
    }
  },
  async fetchWhitelabelConfig({ commit }) {
    // Force the gate closed for this session fetch. Persisted state from a
    // prior load must never leave whitelabelReady=true while the request hangs.
    commit("SET_WHITELABEL_READY", false);
    commit("SET_FAILED_WHITELABEL", false);
    commit("SET_WHITELABEL_LOADING", true);
    try {
      const response = await getWhitelabelSvc().fetchConfig(window.location.hostname);
      commit("SET_WHITELABEL_CONFIG", response.data);
    } catch (err) {
      console.warn("fetchWhitelabelConfig failed; gating app on fatal error.", err);
      commit("SET_WHITELABEL_ERROR", err?.message ?? "Unknown error");
      commit("SET_FAILED_WHITELABEL", true);
      commit("SET_ERROR_STRING", `whitelabel-config-fail: ${err?.message || "unknown"}`);
    } finally {
      // Unlock the App.vue Tier-1 gate whether config loaded or failed fatally
      // (failedWhitelabel routes to the error screen ahead of this flag).
      commit("SET_WHITELABEL_READY", true);
    }
  },
  async fetchUserPermissions({ commit, state }) {
    const activeToken = state.magicLinkToken || state.token;
    if (!activeToken) return;
    if (state.userPermissionsLoading) return;

    commit("SET_USER_PERMISSIONS_LOADING", true);
    try {
      const response = await AuthService.fetchUserPermissions();
      commit("SET_PERMISSIONS_FROM_API", response.result.rows);
    } catch (err) {
      console.warn(
        "fetchUserPermissions API failed; checking JWT fallback.",
        err?.message ?? "Unknown error"
      );
      try {
        const decoded = VueJwtDecode.decode(activeToken);
        const jwtPermissions = decoded.permissions || [];
        if (jwtPermissions.length > 0) {
          console.warn("Using JWT permissions as fallback (API unreachable)");
          commit("SET_PERMISSIONS_FROM_API", jwtPermissions);
        } else {
          console.error("fetchUserPermissions: API failed AND JWT has no permissions");
          commit("SET_FAILED_PERMISSIONS", true);
          commit("SET_ERROR_STRING", "permissions-fail: api-err, no-jwt-fallback");
          commit("SET_PERMISSIONS_FROM_API", []);
        }
      } catch (_decodeErr) {
        console.error("fetchUserPermissions: API failed AND JWT decode failed", _decodeErr);
        commit("SET_FAILED_PERMISSIONS", true);
        commit("SET_ERROR_STRING", "permissions-fail: api-err, jwt-decode-fail");
        commit("SET_PERMISSIONS_FROM_API", []);
      }
    } finally {
      commit("SET_USER_PERMISSIONS_LOADING", false);
    }
  },
  async fetchAppNotification({ commit }) {
    const banner = FeatureFlagService.getAppNotificationData();
    commit("SET_APP_NOTIFICATION", banner);
  },
};

export { actions };

export default new Vuex.Store({
  plugins: [
    createPersistedState({
      reducer: persistedStateReducer,
    }),
  ],
  state: defaultState,
  mutations: {
    RESET_STATE(state) {
      // Used to reset state on app load
      console.log("Resetting state");
      state.loaded = false;
      state.displayWalletMenu = false;
      state.failedLogin = false;
      state.failedWhitelabel = false;
      state.failedPermissions = false;
      // Auth calls resetState on every load, including a fresh login that
      // does not go through LOGOUT. Clears a stuck true already in localStorage.
      state.userPermissionsLoading = false;
      state.errorString = null;
      state.magicLinkToken = null;
      state.magicLinkTokenExpiration = null;
      state.magicLinkRoute = null;
      state.magicLinkTokenExpirationWarning = false;
      state.displayMobilePopup = false;
      state.snackbar = defaultState().snackbar;
      state.pendingUserCount = {
        clientId: null,
        count: null,
      };
    },
    LOGOUT(state) {
      // Used to reset state on app load
      console.log("Erasing cache on logout");

      // Cancel token timeout
      if (state.tokenRefreshTask) clearTimeout(state.tokenRefreshTask);
      if (state.magicLinkTokenRefreshTask) clearTimeout(state.magicLinkTokenRefreshTask);
      if (state.activity.unreadRefreshTask) clearTimeout(state.activity.unreadRefreshTask);
      // window.localStorage.removeItem("vuex");
      Object.assign(state, defaultState());
    },
    SET_PERMISSION_DATA(state, data) {
      var decoded = VueJwtDecode.decode(data);
      state.tokenExpiration = decoded.exp;
      state.roles = decoded.roles || decoded["https://whistle.com/metadata"].roles || [];
      // localStorage.setItem("permissions", JSON.stringify(decoded.permissions));
    },
    SET_PERMISSIONS_FROM_API(state, rows) {
      state.permissions = rows;
    },
    SET_USER_PERMISSIONS_LOADING(state, value) {
      state.userPermissionsLoading = value;
    },
    SET_FEATURE_FLAGS(state, data) {
      state.featureFlags = data;
    },
    SET_FEATURE_FLAGS_RESOLVED(state, value) {
      // Bug #189562 (Cycle 7): monotonic, not a blind assign — once true
      // (e.g. forced open by App.vue's timeout fallback), a later commit
      // with a stale `false` (isReady() hasn't caught up) must not silently
      // re-close the gate. Only LOGOUT's defaultState() reset can lower it.
      state.featureFlagsResolved = state.featureFlagsResolved || value;
    },
    SET_FEATURE_FLAGS_FAILED(state, value) {
      state.featureFlagsFailed = value;
    },
    SET_ENGAGE_CONFIGURATION(state, { configuration, clientId }) {
      state.engageConfiguration = configuration;
      state.engageConfigurationClientId =
        clientId === null || clientId === undefined ? null : String(clientId);
    },
    SET_ENGAGE_CONFIGURATION_LOADING(state, clientId) {
      state.engageConfigurationLoadingClientId =
        clientId === null || clientId === undefined ? null : String(clientId);
    },
    SET_CLIENT_FEATURE_FLAGS(state, { flags, clientId }) {
      state.clientFeatureFlags = flags;
      state.clientFeatureFlagsClientId =
        clientId === null || clientId === undefined ? null : String(clientId);
    },
    SET_DASHBOARD_CONFIG(state, data) {
      state.dashboardConfig = data;
    },
    SET_ERROR_STRING(state, data) {
      state.errorString = data;
    },
    SET_IMPERSONATE(state, data) {
      state.impersonate = data;
    },
    SET_USER(state, data) {
      state.userProfile = data;
      // Vue.set(state, "loaded", true);
      // state.loaded = true;
    },
    SET_CLIENT_ID(state, data) {
      state.globalClientId = data;
    },
    SET_CLIENTS(state, data) {
      try {
        state.clients = data;
      } catch (err) {
        console.log("Error storing clients in localstorage. We'll scrap that idea");
      }
    },
    SET_CASH_BALANCE(state, data) {
      state.cashBalance = data;
    },
    SET_POINTS_BALANCE(state, data) {
      state.pointsBalance = data;
    },
    // SET_PENDING_BALANCE(state, data) {
    //   state.pendingBalance = data;
    // },
    SET_BALANCES(state, data) {
      state.balances = data;
    },
    SET_LOADING_BALANCE(state, data) {
      state.loadingBalance = data;
    },
    SET_TOKEN(state, data) {
      state.token = data;
      axios.defaults.headers.common["Authorization"] = `Bearer ${data}`;
    },
    SET_LOADED(state, data) {
      state.loaded = data;
    },
    SET_FAILED_LOGIN(state, data) {
      state.failedLogin = data;
    },
    SET_SEARCH_ARRAY(state, data) {
      state.searchArray = data;
    },
    SET_LOAD_ITEM_FROM_SEARCH(state, data) {
      state.loadItemFromSearch = data;
    },
    SET_DISPLAY_WALLET_MENU(state, data) {
      state.displayWalletMenu = data;
    },
    SET_WALLET_MENU_SCREEN(state, data) {
      state.walletMenuScreen = data;
    },
    SET_WALLET_INSTRUCTION_TAB(state, data) {
      state.walletInstructionTab = data;
    },
    SET_CONFETTI_PLAYED(state, data) {
      state.confettiPlayed = data;
    },
    SET_SOURCE_WALLET_PAGE(state, data) {
      state.sourceWalletPage = data;
    },
    SET_POST_IT_ARRAY(state, data) {
      state.postItArray.push(data);
    },
    SET_REPORT_TOKENS(state, data) {
      state.reportTokens = data;
    },
    PUSH_REPORT_TOKENS(state, data) {
      state.reportTokens.push(data);
    },
    SET_PROFILE_TAB(state, data) {
      state.profilePageTab = data;
    },
    SET_MARQETA(state, data) {
      state.marqeta = data;
    },
    SET_ACTIVITY_LIST_VIEW(state, data) {
      state.activity.listView = data;
    },
    SET_ACTIVITY_ADMIN_OPTIONS(state, data) {
      state.activity.showAdminOptions = data;
    },
    SET_ACTIVITY_UNREAD(state, data) {
      state.activity.unread = data;
    },
    SET_ACTIVITY_UNREAD_REFRESH_TASK(state, data) {
      state.activity.unreadRefreshTask = data;
    },
    SET_SLACK_WORKSPACE_CORRELATION_ID(state, data) {
      state.slackWorkspaceCorrelationId = data;
    },
    SET_TOKEN_REFRESH_TASK(state, data) {
      state.tokenRefreshTask = data;
    },
    SET_DISPLAY_MOBILE_POPUP(state, data) {
      state.displayMobilePopup = data;
    },
    SET_MAGIC_LINK_TOKEN(state, data) {
      state.magicLinkToken = data;

      oauthAxios.defaults.headers.common["Authorization"] = `Bearer ${data}`;
      // If we're using a magic link token, then we wipe the permissions array
      // if (data) state.permissions = [];
    },
    SET_MAGIC_LINK_EXPIRATION(state, data) {
      state.magicLinkTokenExpiration = data;
    },
    SET_MAGIC_LINK_TOKEN_REFRESH_TASK(state, data) {
      state.magicLinkTokenRefreshTask = data;
    },
    SET_MAGIC_LINK_ROUTE(state, data) {
      state.magicLinkRoute = data;
    },
    SET_MAGIC_LINK_EXPIRATION_WARNING(state, data) {
      state.magicLinkTokenExpirationWarning = data;
    },
    SET_SNACKBAR(state, { text, color = "info", timeout = 4000 }) {
      state.snackbar.text = text;
      state.snackbar.color = color;
      state.snackbar.timeout = timeout;
      state.snackbar.show = true; // Show the snackbar
    },
    // Mutation to explicitly hide the snackbar
    HIDE_SNACKBAR(state) {
      state.snackbar.show = false;
    },
    SET_PENDING_USER_COUNT(state, count) {
      state.pendingUserCount = count;
    },
    SET_WALLET_PROFILE_LOADING(state, value = true) {
      if (typeof state.walletProfile !== "object")
        state.walletProfile = defaultState().walletProfile;
      state.walletProfile.loading = value;
    },
    SET_WALLET_PROFILE(state, { transactions = [], paymentRules = [] }) {
      if (typeof state.walletProfile !== "object")
        state.walletProfile = defaultState().walletProfile;
      state.walletProfile.loading = false;
      state.walletProfile.transactions = transactions;
      state.walletProfile.paymentRules = paymentRules;
    },
    SET_APP_NOTIFICATION(state, data) {
      state.appNotification = data;
    },
    SET_WHITELABEL_CONFIG(state, config) {
      state.whitelabel = {
        ...state.whitelabel,
        applicationName: config.applicationName ?? null,
        supportEmail: config.supportEmail ?? null,
        logoUrl: config.logoUrl ?? null,
        faviconUrl: config.faviconUrl ?? null,
        appStoreUrl: config.appStoreUrl ?? null,
        googlePlayStoreUrl: config.googlePlayStoreUrl ?? null,
        isDefault: config.isDefault === true,
        loading: false,
        error: null,
      };
    },
    SET_WHITELABEL_LOADING(state, value) {
      state.whitelabel = { ...state.whitelabel, loading: value };
    },
    SET_WHITELABEL_ERROR(state, error) {
      state.whitelabel = { ...state.whitelabel, error, loading: false };
    },
    SET_FAILED_WHITELABEL(state, value) {
      state.failedWhitelabel = value;
    },
    SET_FAILED_PERMISSIONS(state, value) {
      state.failedPermissions = value;
    },
    SET_WHITELABEL_READY(state, value) {
      state.whitelabelReady = value;
    },
  },
  actions,
  getters: {
    permissions(state) {
      return state.permissions;
    },
    hasPermission: (state) => (permission) => {
      console.log("has-----");
      console.log(state.permissions);
      return state.permissions.includes(permission);
    },
    ...clientPermissionGetters,
    getUserProfile(state) {
      return state.userProfile;
    },
    appNotification(state) {
      return state.appNotification;
    },
    dashboardConfig(state) {
      return state.dashboardConfig;
    },
  },
  modules: {},
});
